import React, { useState, useRef, useEffect } from "react";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import CustomSelect from "../CommonComponents/CustomSelect";
import axios from "axios";
import { IoMdCloudUpload } from "react-icons/io";
import { RiDeleteBinLine } from "react-icons/ri";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const EMPLOYEE_LIST = gql`
  query getEmployeeList($data: EmployeeFilterInput) {
    getEmployeelist(data: $data) {
      status
      message
      employee {
        id
        fullName
      }
    }
  }
`;
const ADMIN_LIST = gql`
  query {
    getAdminList {
      status
      message
      admins {
        id
        fullName
      }
    }
  }
`;
const GET_INVENTORY_CATEGORIES = gql`
  query getInventoryCategory {
    getInventoryCategory {
      status
      message
      categories {
        id
        name
      }
    }
  }
`;
const ADD_PAYMENT = gql`
  mutation addPayment($data: PaymentInput!) {
    addPayment(data: $data) {
      status
      message
    }
  }
`;

const UPDATE_PAYMENT = gql`
  mutation updatePayment($id: ID!, $data: UpdatePaymentInput!) {
    updatePayment(id: $id, data: $data) {
      status
      message
    }
  }
`;

const GET_DEALERS = gql`
  query getDealers {
    getDealers {
      id
      name
    }
  }
`;

const ADD_DEALER = gql`
  mutation addDealer($name: String!) {
    addDealer(name: $name) {
      id
      name
    }
  }
`;

const GET_GST_RATES = gql`
  query getGstRates {
    getGstRates {
      id
      percentage
    }
  }
`;

const ADD_GST_RATE = gql`
  mutation addGstRate($percentage: Float!) {
    addGstRate(percentage: $percentage) {
      id
      percentage
    }
  }
`;

const FIND_DUPLICATE_BILLS = gql`
  query findDuplicateBills($data: AdminPaymentFilter) {
    getPayments(data: $data) {
      payments {
        id
        amount
        billDate
        dealerName
      }
    }
  }
`;

const MIN_ENTRY_YEAR = 2020;

// Catches typing slips such as year 0006 or 52026
const isReasonableDate = (value) => {
  const year = new Date(value).getFullYear();
  return year >= MIN_ENTRY_YEAR && year <= new Date().getFullYear() + 1;
};

const getReadableErrorMessage = (error) => {
  if (!error) {
    return "Something went wrong. Please try again.";
  }

  const graphQLErrorMessage = error.graphQLErrors?.[0]?.message;
  if (graphQLErrorMessage) {
    return graphQLErrorMessage;
  }

  const networkErrorMessage =
    error.networkError?.result?.errors?.[0]?.message ||
    error.networkError?.result?.message ||
    error.networkError?.message;

  if (networkErrorMessage) {
    return networkErrorMessage;
  }

  return error.message || "Something went wrong. Please try again.";
};

const PROFESSIONAL_SALARY_CATEGORY = "Professional Salary";
const PROFESSIONAL_ADVANCE_PAYMENT_CATEGORY = "Professional Advance Payment";
const ADMIN_SALARY_CATEGORY = "Admin Salary";
const OTHER_EXPENSES_CATEGORY = "Other Expenses";
const INVENTORY_PURCHASE_CATEGORY = "Inventory Purchase";

const EMPTY_INVENTORY_ROW = {
  inventoryCategoryId: "",
  quantity: "",
  rate: "",
  mrp: "",
  discount: "",
  gstPercentage: "",
};

const isCategoryMatch = (value, target) =>
  String(value || "")
    .trim()
    .toLowerCase() ===
  String(target || "")
    .trim()
    .toLowerCase();

const getEntityId = (value) => {
  if (value == null) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object") {
    if (value.id != null) {
      return String(value.id);
    }

    if (value._id != null) {
      return String(value._id);
    }
  }

  return "";
};

const PaymentForm = ({ setOpenModal, existingPayment = null, onSuccess }) => {
  const isEdit = !!existingPayment;
  const { data } = useQuery(EMPLOYEE_LIST, {
    variables: {
      data: {
        bookableOrEmployed: true,
      },
    },
  });
  const { data: adminData } = useQuery(ADMIN_LIST);
  const { data: inventoryCategoryData } = useQuery(GET_INVENTORY_CATEGORIES);
  const { data: dealersData, refetch: refetchDealers } = useQuery(GET_DEALERS);
  const { data: gstRatesData, refetch: refetchGstRates } = useQuery(GET_GST_RATES);

  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [billDate, setBillDate] = useState("");
  const [isPaymentDue, setIsPaymentDue] = useState(false);
  const [type, setType] = useState("Debit");
  const [category, setCategory] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [description, setDescription] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [adminId, setAdminId] = useState("");
  const [payerName, setPayerName] = useState("");
  const [otherCategory, setOtherCategory] = useState("");
  const [employees, setEmployees] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [inventories, setInventories] = useState([{ ...EMPTY_INVENTORY_ROW }]);
  const [dealerName, setDealerName] = useState("");
  const fileInputRef = useRef(null);
  const paymentCategories = [
    "Professional Salary",
    "Admin Salary",
    "Office Rent",
    "Professional Advance Payment",
    "Inventory Purchase",
    "AWS/Cloud Services",
    "SMS/Email Recharge",
    "Marketing Expenses",
    "Other Expenses",
  ];
  const creditCategories = ["Booking Payment", "Funding", "Other Credits"];
  const paymentMethods = ["Cash", "Bank Transfer", "UPI", "Card", "Other"];
  const [isInventoryPurchase, setIsInventoryPurchase] = useState(false);
  const [isGstBill, setIsGstBill] = useState(false);
  const hasExistingInventoryDetails = Array.isArray(
    existingPayment?.inventoryPayments,
  );

  const [addPayment] = useMutation(ADD_PAYMENT);
  const [updatePayment] = useMutation(UPDATE_PAYMENT);
  const [addDealer] = useMutation(ADD_DEALER);
  const [addGstRate] = useMutation(ADD_GST_RATE);
  const [findDuplicateBills] = useLazyQuery(FIND_DUPLICATE_BILLS, {
    fetchPolicy: "network-only",
  });

  const buildInventoryRowsFromPayment = (payment) => {
    const mappedRows =
      payment?.inventoryPayments?.map((item) => ({
        inventoryCategoryId: item.inventoryCategoryId || "",
        quantity: item.quantity != null ? String(item.quantity) : "",
        rate: item.rate != null ? String(item.rate) : "",
        mrp: item.mrp != null ? String(item.mrp) : "",
        discount: item.discount != null ? String(item.discount) : "",
        gstPercentage:
          item.gstPercentage != null ? String(item.gstPercentage) : "",
      })) || [];
    console.log("Mapped inventory rows from payment: ", mappedRows);

    return mappedRows.length > 0 ? mappedRows : [{ ...EMPTY_INVENTORY_ROW }];
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    e.preventDefault();
    const url = `https://api.barbera.in/img/upload`;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(url, formData);
      if (data.success === true) {
        alert("Bill uploaded successfully");
        setAttachmentUrl(data.url);
      }
    } catch (error) {
      console.error(error);
      window.alert(error.message + " Try Again");
    }
  };

  useEffect(() => {
    if (existingPayment) {
      setAmount(existingPayment.amount || "");
      setDate(existingPayment.date ? existingPayment.date.slice(0, 10) : "");
      setBillDate(
        existingPayment.billDate ? existingPayment.billDate.slice(0, 10) : "",
      );
      setIsPaymentDue(existingPayment.isPaymentDue || false);
      setType(existingPayment.type || "Debit");
      setCategory(existingPayment.category || "");
      setOtherCategory(existingPayment.otherCategory || "");
      setPaymentMethod(existingPayment.paymentMethod || "");
      setDescription(existingPayment.description || "");
      setAttachmentUrl(existingPayment.attachmentUrl || "");
      setEmployeeId(
        getEntityId(existingPayment.employeeId) ||
          getEntityId(existingPayment.employee),
      );
      setAdminId(
        getEntityId(existingPayment.adminId) ||
          getEntityId(existingPayment.admin),
      );
      setPayerName(existingPayment.payerName || "");
      setDealerName(existingPayment.dealerName || "");
      setIsGstBill(existingPayment.isGstBill || false);
      setIsInventoryPurchase(
        isCategoryMatch(existingPayment.category, INVENTORY_PURCHASE_CATEGORY),
      );
      setInventories(buildInventoryRowsFromPayment(existingPayment));
    } else {
      setInventories([{ ...EMPTY_INVENTORY_ROW }]);
      setIsInventoryPurchase(false);
      setIsGstBill(false);
      setBillDate("");
      setIsPaymentDue(false);
    }
  }, [existingPayment]);

  useEffect(() => {
    setEmployees(data?.getEmployeelist?.employee || []);
  }, [data]);

  useEffect(() => {
    setAdmins(adminData?.getAdminList?.admins || []);
  }, [adminData]);

  useEffect(() => {
    const categories = [
      ...(inventoryCategoryData?.getInventoryCategory?.categories || []),
    ];
    categories.sort((a, b) => a.name.localeCompare(b.name));
    setInventoryCategories(categories);
  }, [inventoryCategoryData]);

  useEffect(() => {
    if (type === "Credit") {
      setEmployeeId("");
      setAdminId("");
    }

    if (type === "Credit" && payerName) {
      setPayerName("");
    }
  }, [type]);

  useEffect(() => {
    if (
      !isCategoryMatch(category, PROFESSIONAL_SALARY_CATEGORY) &&
      !isCategoryMatch(category, PROFESSIONAL_ADVANCE_PAYMENT_CATEGORY)
    ) {
      setEmployeeId("");
    }

    if (!isCategoryMatch(category, ADMIN_SALARY_CATEGORY)) {
      setAdminId("");
    }

    if (!isCategoryMatch(category, OTHER_EXPENSES_CATEGORY)) {
      setOtherCategory("");
    }

    if (!isCategoryMatch(category, INVENTORY_PURCHASE_CATEGORY) && !isEdit) {
      setInventories([{ ...EMPTY_INVENTORY_ROW }]);
    }
  }, [category, isEdit]);

  useEffect(() => {
    if (!isEdit || !existingPayment) {
      return;
    }

    if (
      (isCategoryMatch(category, PROFESSIONAL_SALARY_CATEGORY) ||
        isCategoryMatch(category, PROFESSIONAL_ADVANCE_PAYMENT_CATEGORY)) &&
      !employeeId
    ) {
      const resolvedEmployeeId =
        getEntityId(existingPayment.employeeId) ||
        getEntityId(existingPayment.employee);

      if (resolvedEmployeeId) {
        setEmployeeId(resolvedEmployeeId);
      }
    }

    if (isCategoryMatch(category, ADMIN_SALARY_CATEGORY) && !adminId) {
      const resolvedAdminId =
        getEntityId(existingPayment.adminId) ||
        getEntityId(existingPayment.admin);

      if (resolvedAdminId) {
        setAdminId(resolvedAdminId);
      }
    }
  }, [
    isEdit,
    existingPayment,
    category,
    employeeId,
    adminId,
    employees,
    admins,
  ]);

  useEffect(() => {
    // While editing, Amount stays as saved unless the admin changes it
    if (!isInventoryPurchase || isEdit) {
      return;
    }

    const computedTotal = inventories.reduce((sum, item) => {
      const rateValue = parseFloat(item.rate);
      if (!rateValue || rateValue <= 0) {
        return sum;
      }
      const gstValue = isGstBill ? parseFloat(item.gstPercentage) || 0 : 0;
      return sum + rateValue + (rateValue * gstValue) / 100;
    }, 0);

    if (computedTotal > 0) {
      setAmount(String(Math.round(computedTotal * 100) / 100));
    }
  }, [inventories, isGstBill, isInventoryPurchase, isEdit]);

  const handleInventoryChange = (index, field, value) => {
    setInventories((currentRows) =>
      currentRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const handleAddInventoryRow = () => {
    setInventories((currentRows) => [
      ...currentRows,
      { ...EMPTY_INVENTORY_ROW },
    ]);
  };

  const handleRemoveInventoryRow = (index) => {
    setInventories((currentRows) => {
      if (currentRows.length === 1) {
        return [{ ...EMPTY_INVENTORY_ROW }];
      }

      return currentRows.filter((_, rowIndex) => rowIndex !== index);
    });
  };

  const normalizeInventories = () => {
    if (!isInventoryPurchase) {
      return undefined;
    }

    const normalizedRows = inventories
      .filter(
        (item) =>
          item.inventoryCategoryId &&
          item.quantity !== "" &&
          item.rate !== "" &&
          item.mrp !== "" &&
          item.discount !== "",
      )
      .map((item) => ({
        inventoryCategoryId: item.inventoryCategoryId,
        quantity: Number(item.quantity),
        rate: parseFloat(item.rate),
        mrp: item.mrp !== "" ? parseFloat(item.mrp) : null,
        discount: item.discount !== "" ? parseFloat(item.discount) : null,
        gstPercentage:
          isGstBill && item.gstPercentage !== ""
            ? parseFloat(item.gstPercentage)
            : null,
      }));

    if (normalizedRows.length > 0) {
      return normalizedRows;
    }

    if (isEdit) {
      return hasExistingInventoryDetails ? [] : undefined;
    }

    return [];
  };

  const validateForm = () => {
    if (!amount || Number(amount) <= 0) {
      alert("Please enter a valid amount");
      return false;
    }

    if (!billDate) {
      alert("Please enter bill date");
      return false;
    }

    if (!isPaymentDue && !date) {
      alert("Please enter payment date");
      return false;
    }

    if (
      !isReasonableDate(billDate) ||
      (!isPaymentDue && !isReasonableDate(date))
    ) {
      alert("Please check the year in the bill date and payment date");
      return false;
    }

    if (!category) {
      alert("Please select a category");
      return false;
    }

    if (!isPaymentDue && !paymentMethod) {
      alert("Please select a payment method");
      return false;
    }

    if (
      isCategoryMatch(category, OTHER_EXPENSES_CATEGORY) &&
      !otherCategory.trim()
    ) {
      alert("Please enter the other expense name");
      return false;
    }

    if (
      (isCategoryMatch(category, PROFESSIONAL_SALARY_CATEGORY) ||
        isCategoryMatch(category, PROFESSIONAL_ADVANCE_PAYMENT_CATEGORY)) &&
      !employeeId
    ) {
      alert("Please select a professional");
      return false;
    }

    if (isCategoryMatch(category, ADMIN_SALARY_CATEGORY) && !adminId) {
      alert("Please select an admin");
      return false;
    }

    if (type === "Debit" && !payerName.trim()) {
      alert("Please enter payer name");
      return false;
    }
    if (attachmentUrl.length === 0 && type === "Debit") {
      alert("Please upload an valid bill for debit payments");
      return false;
    }
    if (isInventoryPurchase && !dealerName.trim()) {
      alert("Please enter dealer name for inventory purchases");
      return false;
    }

    if (isInventoryPurchase && (!isEdit || hasExistingInventoryDetails)) {
      const normalizedInventories = normalizeInventories();

      if (normalizedInventories.length === 0) {
        alert("Please add at least one inventory line item");
        return false;
      }

      const hasInvalidRow = normalizedInventories.some(
        (item) =>
          item.quantity <= 0 ||
          item.rate <= 0 ||
          item.mrp <= 0 ||
          item.discount < 0,
      );

      if (hasInvalidRow) {
        alert("Inventory quantity, total amount, and MRP must be greater than 0");
        return false;
      }
    }

    return true;
  };

  // A failed check shouldn't stop the entry from being saved
  const hasDuplicateBill = async () => {
    if (!isInventoryPurchase) {
      return false;
    }

    try {
      const { data: result } = await findDuplicateBills({
        variables: {
          data: {
            category,
            billStartDate: billDate,
            billEndDate: billDate,
          },
        },
      });

      return (result?.getPayments?.payments || []).some(
        (payment) =>
          payment.id !== existingPayment?.id &&
          Number(payment.amount) === Number(amount) &&
          isCategoryMatch(payment.dealerName, dealerName),
      );
    } catch (err) {
      console.error("Duplicate check failed:", err);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (
      (await hasDuplicateBill()) &&
      !window.confirm(
        `A bill from ${dealerName} for ₹ ${amount} with this bill date already exists. Save anyway?`,
      )
    ) {
      return;
    }

    const normalizedInventories = normalizeInventories();

    const isProfessionalSalary =
      isCategoryMatch(category, PROFESSIONAL_SALARY_CATEGORY) ||
      isCategoryMatch(category, PROFESSIONAL_ADVANCE_PAYMENT_CATEGORY);
    const isAdminSalary = isCategoryMatch(category, ADMIN_SALARY_CATEGORY);
    const isOtherExpense = isCategoryMatch(category, OTHER_EXPENSES_CATEGORY);

    const baseData = {
      amount: Number(amount),
      date: !isPaymentDue && date ? new Date(date).toISOString() : null,
      billDate: billDate ? new Date(billDate).toISOString() : null,
      isPaymentDue,
      type,
      category,
      otherCategory: isOtherExpense ? otherCategory : "",
      paymentMethod: isPaymentDue ? null : paymentMethod,
      description,
      attachmentUrl,
      employeeId: isProfessionalSalary ? employeeId || null : null,
      adminId: isAdminSalary ? adminId || null : null,
      payerName: type === "Debit" ? payerName : "",
      dealerName: isInventoryPurchase ? dealerName : "",
      isGstBill: isInventoryPurchase ? isGstBill : false,
    };

    if (normalizedInventories !== undefined) {
      baseData.inventories = normalizedInventories;
    }

    try {
      const res = isEdit
        ? await updatePayment({
            variables: {
              id: existingPayment.id,
              data: baseData,
            },
          })
        : await addPayment({
            variables: {
              data: baseData,
            },
          });

      const result = isEdit ? res.data.updatePayment : res.data.addPayment;

      if (result.status === "SUCCESS") {
        alert(`Payment ${isEdit ? "updated" : "added"} successfully`);
        setOpenModal(false);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        alert(result.message);
      }
    } catch (err) {
      console.error("Error:", err);
      alert(getReadableErrorMessage(err));
    }
  };

  const selectedEmployeeId = String(employeeId || "");
  const selectedAdminId = String(adminId || "");

  const employeeOptions =
    selectedEmployeeId &&
    !employees.some((employee) => String(employee.id) === selectedEmployeeId)
      ? [
          ...employees,
          {
            id: selectedEmployeeId,
            fullName:
              existingPayment?.employee?.fullName || "Selected Professional",
          },
        ]
      : employees;

  const adminOptions =
    selectedAdminId &&
    !admins.some((admin) => String(admin.id) === selectedAdminId)
      ? [
          ...admins,
          {
            id: selectedAdminId,
            fullName: existingPayment?.admin?.fullName || "Selected Admin",
          },
        ]
      : admins;

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col space-y-6 bg-white text-gray-800 rounded-lg"
    >
      {/* HEADER */}

      <div className="flex justify-between items-center border-b pb-3">
        <h3 className="text-xl font-semibold text-[#FBB033]">
          {isEdit ? "Edit Payment" : "Add Payment"}
        </h3>

        <button
          type="button"
          onClick={() => setOpenModal(false)}
          className="text-gray-500 hover:text-black text-lg"
        >
          ✕
        </button>
      </div>

      {existingPayment?.settlement && (
        <p className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          This bill is part of a linked payment. Unlink it from the payment
          details to change the amount, dealer, payment date or method.
        </p>
      )}

      {/* FORM GRID */}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* Amount */}

        <div className="flex flex-col">
          <label className="text-sm mb-1 text-gray-600">Amount</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-amber-300 outline-none"
          />
        </div>

        {/* Bill Date */}

        <div className="flex flex-col">
          <label className="text-sm mb-1 text-gray-600">Bill Date</label>
          <input
            type="date"
            required
            value={billDate}
            onChange={(e) => setBillDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-amber-300 outline-none"
          />
        </div>

        {/* Payment Date */}

        <div className="flex flex-col">
          <label className="text-sm mb-1 text-gray-600">
            Payment Date{isPaymentDue ? " (payment due)" : ""}
          </label>
          <input
            type="date"
            required={!isPaymentDue}
            disabled={isPaymentDue}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-amber-300 outline-none disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          />
        </div>

        {/* Payment Due Checkbox */}
        
        {type === "Debit" && (
          <div className="flex items-center col-span-1 sm:col-span-2 mt-2">
            <input
              type="checkbox"
              id="isPaymentDue"
              checked={isPaymentDue}
              onChange={(e) => {
                const checked = e.target.checked;
                setIsPaymentDue(checked);
                if (checked) {
                  setDate(""); // Clear payment date if due
                }
              }}
              className="w-4 h-4 text-amber-500 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
            />
            <label htmlFor="isPaymentDue" className="ml-2 text-sm font-medium text-gray-700">
              Payment Due (check if payment is not yet made)
            </label>
          </div>
        )}

        {/* Payment Type */}

        <CustomSelect
          label="Payment Type"
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            // Only debits can be due; the checkbox is hidden for credits
            if (e.target.value !== "Debit") {
              setIsPaymentDue(false);
            }
          }}
          options={["Debit", "Credit"]}
          inline
          mode="light"
        />

        {/* Category */}

        <CustomSelect
          label="Payment Category"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setIsInventoryPurchase(
              isCategoryMatch(e.target.value, INVENTORY_PURCHASE_CATEGORY),
            );
          }}
          options={type === "Debit" ? paymentCategories : creditCategories}
          mode="light"
        />

        {isCategoryMatch(category, OTHER_EXPENSES_CATEGORY) && (
          <div className="flex flex-col">
            <label className="text-sm mb-1 text-gray-600">Other Expense</label>
            <input
              type="text"
              value={otherCategory}
              onChange={(e) => setOtherCategory(e.target.value)}
              placeholder="Enter expense name"
              className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-amber-300 outline-none"
            />
          </div>
        )}

        {/* Professional */}

        {(isCategoryMatch(category, PROFESSIONAL_SALARY_CATEGORY) ||
          isCategoryMatch(category, PROFESSIONAL_ADVANCE_PAYMENT_CATEGORY)) && (
          <CustomSelect
            label="Professional"
            value={employeeId}
            optionLabelKey="fullName"
            optionValueKey="id"
            onChange={(e) => setEmployeeId(e.target.value)}
            options={employeeOptions}
            mode="light"
          />
        )}

        {/* Admin */}

        {isCategoryMatch(category, ADMIN_SALARY_CATEGORY) && (
          <CustomSelect
            label="Admin"
            value={adminId}
            optionLabelKey="fullName"
            optionValueKey="id"
            onChange={(e) => setAdminId(e.target.value)}
            options={adminOptions}
            mode="light"
          />
        )}

        {/* Method */}

        {!isPaymentDue && (
          <CustomSelect
            label="Payment Method"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            options={paymentMethods}
            mode="light"
          />
        )}

        {/* Payer */}

        {type === "Debit" && (
          <div className="flex flex-col" style={{ zIndex: 100 }}>
            <label className="text-sm mb-1 text-gray-600">Payer Name</label>
            <CreatableSelect
              isClearable
              options={admins.map((admin) => ({
                value: admin.fullName,
                label: admin.fullName,
              }))}
              value={
                payerName ? { value: payerName, label: payerName } : null
              }
              onChange={(val) => setPayerName(val ? val.value : "")}
              onCreateOption={(inputValue) => setPayerName(inputValue)}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "42px",
                  borderColor: "#D1D5DB",
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "#D1D5DB",
                  },
                }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
              placeholder="Select an admin or type a name..."
            />
          </div>
        )}
        {isInventoryPurchase && (
          <div className="flex flex-col" style={{ zIndex: 100 }}>
            <label className="text-sm mb-1 text-gray-600">Dealer Name</label>
            <CreatableSelect
              isClearable
              options={
                dealersData?.getDealers?.map((d) => ({ value: d.name, label: d.name })) || []
              }
              value={
                dealerName ? { value: dealerName, label: dealerName } : null
              }
              onChange={(val) => setDealerName(val ? val.value : "")}
              onCreateOption={async (inputValue) => {
                try {
                  const res = await addDealer({ variables: { name: inputValue } });
                  if (res.data.addDealer) {
                    await refetchDealers();
                    setDealerName(res.data.addDealer.name);
                  }
                } catch (err) {
                  alert("Failed to create dealer: " + err.message);
                }
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  borderColor: '#D1D5DB',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: '#D1D5DB'
                  }
                }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
              placeholder="Select or type new..."
            />
          </div>
        )}
        {isInventoryPurchase && (
          <div className="flex flex-col items-start justify-end pb-2">
            <label className="flex items-center space-x-2 cursor-pointer mt-5">
              <input
                type="checkbox"
                checked={isGstBill}
                onChange={(e) => setIsGstBill(e.target.checked)}
                className="form-checkbox h-5 w-5 text-amber-500 rounded focus:ring-amber-300 border-gray-300"
              />
              <span className="text-sm text-gray-600 font-medium">Is GST Bill?</span>
            </label>
          </div>
        )}
      </div>

      {isInventoryPurchase && (
        <div className="space-y-4 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-800">Inventory Items</h4>
              <p className="text-sm text-gray-500">
                Add quantity and rate for each purchased inventory category.
              </p>
              {isEdit && !hasExistingInventoryDetails && (
                <p className="mt-1 text-xs text-amber-600">
                  Existing inventory line items are not available from the
                  current payments query. Add rows here only if you want to
                  replace them.
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handleAddInventoryRow}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              + Add Item
            </button>
          </div>

          <div className="space-y-3">
            {inventories.map((item, index) => {
              const selectedCategory = inventoryCategories.find(
                (categoryOption) =>
                  categoryOption.id === item.inventoryCategoryId,
              );

              return (
                <div
                  key={`${item.inventoryCategoryId || "inventory"}-${index}`}
                  className="flex flex-wrap items-end gap-3 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0"
                >
                  <div className="flex flex-1 min-w-[180px] flex-col">
                    <label className="text-sm text-gray-600 mb-1">Inventory Category</label>
                    <Select
                      value={
                        inventoryCategories
                          .map((cat) => ({ value: cat.id, label: cat.name }))
                          .find(
                            (opt) => opt.value === item.inventoryCategoryId,
                          ) || null
                      }
                      onChange={(selectedOption) => {
                        handleInventoryChange(
                          index,
                          "inventoryCategoryId",
                          selectedOption ? selectedOption.value : "",
                        );
                      }}
                      options={inventoryCategories.map((cat) => ({
                        value: cat.id,
                        label: cat.name,
                      }))}
                      placeholder="Select an option"
                      isSearchable={true}
                      isClearable={false}
                      className="text-sm text-black"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: "42px",
                          borderRadius: "0.375rem",
                          borderColor: state.isFocused ? "#FBB033" : "#4b5563",
                          boxShadow: state.isFocused ? "0 0 0 1px #FBB033" : "none",
                          "&:hover": {
                            borderColor: state.isFocused ? "#FBB033" : "#4b5563",
                          },
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>

                  <div className="flex w-24 min-w-[90px] flex-col">
                    <label className="text-sm mb-1 text-gray-600">Qty</label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) =>
                        handleInventoryChange(index, "quantity", e.target.value)
                      }
                      className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-amber-300 outline-none"
                    />
                  </div>

                  <div className="flex w-28 min-w-[100px] flex-col">
                    <label className="text-sm mb-1 text-gray-600">MRP</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.mrp}
                      onChange={(e) =>
                        handleInventoryChange(index, "mrp", e.target.value)
                      }
                      className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-amber-300 outline-none"
                    />
                  </div>

                  <div className="flex w-32 min-w-[110px] flex-col">
                    <label className="text-sm mb-1 text-gray-600">
                      Total Amount
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.rate}
                      onChange={(e) =>
                        handleInventoryChange(index, "rate", e.target.value)
                      }
                      className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-amber-300 outline-none"
                    />
                  </div>

                  <div className="flex w-28 min-w-[100px] flex-col">
                    <label className="text-sm mb-1 text-gray-600">
                      Discount
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.discount}
                      onChange={(e) =>
                        handleInventoryChange(index, "discount", e.target.value)
                      }
                      className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-amber-300 outline-none"
                    />
                  </div>

                  {isGstBill && (
                    <div
                      className="flex w-32 min-w-[120px] flex-col"
                      style={{ zIndex: 100 }}
                    >
                      <label className="text-sm mb-1 text-gray-600">
                        GST %
                      </label>
                      <CreatableSelect
                        isClearable
                        options={(gstRatesData?.getGstRates || []).map(
                          (rate) => ({
                            value: String(rate.percentage),
                            label: `${rate.percentage}%`,
                          }),
                        )}
                        value={
                          item.gstPercentage
                            ? {
                                value: item.gstPercentage,
                                label: `${item.gstPercentage}%`,
                              }
                            : null
                        }
                        onChange={(val) =>
                          handleInventoryChange(
                            index,
                            "gstPercentage",
                            val ? val.value : "",
                          )
                        }
                        onCreateOption={async (inputValue) => {
                          const percentage = parseFloat(inputValue);
                          if (Number.isNaN(percentage) || percentage < 0) {
                            alert("Please enter a valid GST percentage");
                            return;
                          }
                          try {
                            const res = await addGstRate({
                              variables: { percentage },
                            });
                            if (res.data.addGstRate) {
                              await refetchGstRates();
                              handleInventoryChange(
                                index,
                                "gstPercentage",
                                String(res.data.addGstRate.percentage),
                              );
                            }
                          } catch (err) {
                            alert("Failed to add GST rate: " + err.message);
                          }
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: "42px",
                            borderColor: "#D1D5DB",
                            boxShadow: "none",
                            "&:hover": {
                              borderColor: "#D1D5DB",
                            },
                          }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        placeholder="Add %"
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => handleRemoveInventoryRow(index)}
                    className="rounded-md border border-gray-300 p-2 text-base text-red-500 hover:bg-red-50"
                    aria-label="Delete inventory row"
                    title="Delete"
                  >
                    <RiDeleteBinLine />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DESCRIPTION */}

      <div className="flex flex-col">
        <label className="text-sm mb-1 text-gray-600">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-amber-300 outline-none"
        />
      </div>

      {/* FILE UPLOAD */}

      <div className="flex items-center gap-4">
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleImageUpload}
        />

        <button
          type="button"
          onClick={handleBrowseClick}
          className="flex items-center gap-2 px-3 py-2 border rounded-md bg-gray-100 hover:bg-gray-200"
        >
          <IoMdCloudUpload />
          Upload Bill
        </button>

        {attachmentUrl && (
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://api.barbera.in${attachmentUrl}`}
            className="text-[#FBB033]"
          >
            View
          </a>
        )}
      </div>

      {/* FOOTER */}

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={() => setOpenModal(false)}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100 transition"
        >
          Cancel
        </button>

        <button
          type="submit"
          className="bg-[#FBB033] px-5 py-2 rounded-md font-semibold text-black"
        >
          {isEdit ? "Update Payment" : "Add Payment"}
        </button>
      </div>
    </form>
  );
};

export default PaymentForm;
